import { defineMutator } from "@rocicorp/zero"

import type { MutatorTx } from "../schema"
import type { GithubRepositoryArgs, LinkGithubRepository } from "../types"

// Reactive mutator for linking a repository to existing GithubSettings. The
// row itself is created server-side (tRPC `github.complete` via the install
// callback) and torn down server-side too (`github.uninstall`, which also
// revokes the App install on GitHub) — both need GitHub-App tokens/env that
// can't live on the client. Picking a repo is a plain DB update, so it syncs
// through Zero.
export const githubSettingsMutators = {
	link: defineMutator(
		async ({ tx, args }: { tx: MutatorTx; args: LinkGithubRepository }) => {
			const [existing] = await tx.query.githubSettings
				.where("id", "=", args.id)
				.run()
			if (!existing) {
				throw new Error("GitHub settings not found for this workspace")
			}
			await tx.mutate.githubSettings.update({
				id: args.id,
				repositoryFullName: args.repositoryFullName,
				updatedAt: Date.now(),
			})
		},
	),

	// The first repo becomes the primary one; later repos join the list.
	addRepository: defineMutator(
		async ({ tx, args }: { tx: MutatorTx; args: GithubRepositoryArgs }) => {
			const [existing] = await tx.query.githubSettings
				.where("id", "=", args.id)
				.run()
			if (!existing) {
				throw new Error("GitHub settings not found for this workspace")
			}
			const additional = existing.additionalRepositories ?? []
			if (!existing.repositoryFullName) {
				await tx.mutate.githubSettings.update({
					id: args.id,
					repositoryFullName: args.repositoryFullName,
					updatedAt: Date.now(),
				})
				return
			}
			const key = args.repositoryFullName.toLowerCase()
			const linked = [existing.repositoryFullName, ...additional]
			if (linked.some((name) => name.toLowerCase() === key)) return
			await tx.mutate.githubSettings.update({
				id: args.id,
				additionalRepositories: [...additional, args.repositoryFullName],
				updatedAt: Date.now(),
			})
		},
	),

	// Removing the primary repo promotes the next linked one in its place.
	removeRepository: defineMutator(
		async ({ tx, args }: { tx: MutatorTx; args: GithubRepositoryArgs }) => {
			const [existing] = await tx.query.githubSettings
				.where("id", "=", args.id)
				.run()
			if (!existing) {
				throw new Error("GitHub settings not found for this workspace")
			}
			const key = args.repositoryFullName.toLowerCase()
			const additional = existing.additionalRepositories ?? []
			const remaining = additional.filter((name) => name.toLowerCase() !== key)
			const isPrimary = existing.repositoryFullName?.toLowerCase() === key
			await tx.mutate.githubSettings.update({
				id: args.id,
				...(isPrimary
					? {
							repositoryFullName: remaining[0] ?? null,
							additionalRepositories: remaining.slice(1),
						}
					: { additionalRepositories: remaining }),
				updatedAt: Date.now(),
			})
		},
	),
}
